import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ButcherProfile = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [butcher, setButcher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;
  const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:4000';

  useEffect(() => {
    const fetchButcher = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/butcher/${id}`);
        if (!res.ok) throw new Error('Butcher not found');
        const data = await res.json();
        setButcher(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchButcher();
  }, [id, API_URL]);

  const getImageUrl = (url) => {
    if (!url) return '/assets/placeholder.png';
    if (url.startsWith('http')) return url;
    return `${BASE_URL}${url}`;
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  if (error || !butcher) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <p className="text-xl font-bold">Butcher not found</p>
        <button onClick={() => navigate('/search')}
          className="px-6 py-3 bg-primary text-white rounded-full font-bold">
          Back to Marketplace
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background text-slate-900 font-body antialiased">
      <Navbar variant="default" />

      <main className="pt-20">

        {/* Hero */}
        <section className="relative h-[85vh] min-h-[700px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="/assets/auction-hero(1).jpg"
              alt="Butcher Shop"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0"
              style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.1) 100%)' }} />
          </div>

          <div className="relative z-10 w-full max-w-screen-2xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Name & Actions */}
            <div className="space-y-6">
              <div className="flex items-center gap-8">
                <div className="relative flex-shrink-0">
                  <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-full border-4 border-white/30 p-1 overflow-hidden shadow-2xl"
                    style={{ backdropFilter: 'blur(4px)' }}>
                    <img
                      src={butcher.profile_image
                        ? (butcher.profile_image.startsWith('http') ? butcher.profile_image : `${BASE_URL}${butcher.profile_image}`)
                        : `https://ui-avatars.com/api/?name=${encodeURIComponent(butcher.name)}&size=200&background=06A788&color=fff`}
                      alt={butcher.name}
                      className="w-full h-full object-cover rounded-full"
                    />
                  </div>
                  <div className="absolute -bottom-1 right-2 bg-emerald-400 text-white w-8 h-8 lg:w-10 lg:h-10 rounded-full flex items-center justify-center border-4 border-white shadow-lg">
                    <span className="material-symbols-outlined text-sm lg:text-base"
                      style={{ fontVariationSettings: "'FILL' 1" }}>verified</span>
                  </div>
                </div>
                <h1 className="text-6xl lg:text-8xl font-headline font-extrabold text-white tracking-tight leading-none">
                  {butcher.name.split(' ')[0]} <br />
                  <span style={{ color: '#06A788' }}>{butcher.name.split(' ').slice(1).join(' ')}</span>
                </h1>
              </div>

              <p className="flex items-center gap-2 text-white/80 font-medium text-lg">
                <span className="material-symbols-outlined text-emerald-400">location_on</span>
                {[butcher.city, butcher.state].filter(Boolean).join(', ')} • Since {new Date(butcher.member_since).getFullYear()}
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <button className="bg-white text-primary px-8 py-4 rounded-full font-bold flex items-center gap-3 shadow-2xl hover:bg-slate-50 transition-all">
                  <span className="material-symbols-outlined">mail</span> Send Message
                </button>
                <button className="bg-[#25D366] text-white px-8 py-4 rounded-full font-bold flex items-center gap-3 shadow-2xl hover:scale-105 transition-transform">
                  <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.316 1.592 5.43 0 9.856-4.426 9.858-9.855.002-5.43-4.425-9.856-9.853-9.856-5.417 0-9.856 4.425-9.859 9.854-.001 2.09.547 3.567 1.461 5.145l-1.08 3.945 4.107-1.08z"/>
                  </svg>
                  WhatsApp Butcher
                </button>
              </div>
            </div>

            {/* Right: Stats Card */}
            <div className="lg:justify-self-end">
              <div className="p-10 rounded-3xl w-full max-w-md text-white space-y-8"
                style={{
                  background: 'rgba(255,255,255,0.75)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  boxShadow: '0 30px 60px -15px rgba(0,107,86,0.08)'
                }}>
                <h3 className="text-xs font-bold text-primary uppercase tracking-[0.3em]">Shop Statistics</h3>
                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-1">Since</p>
                    <p className="text-4xl font-headline font-bold text-slate-900">
                      {new Date(butcher.member_since).getFullYear()}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-1">Deliveries</p>
                    <p className="text-4xl font-headline font-bold text-slate-900">
                      {butcher.total_sales}<span className="text-primary text-2xl">+</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-1">Products</p>
                    <p className="text-4xl font-headline font-bold text-slate-900">
                      {butcher.total_products}<span className="text-primary text-2xl"> Live</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-1">Rating</p>
                    <div className="flex items-center gap-1">
                      <p className="text-4xl font-headline font-bold text-slate-900">4.9</p>
                      <span className="material-symbols-outlined text-primary"
                        style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-slate-200">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary">verified_user</span>
                    <p className="text-xs font-medium text-slate-600">Verified & certified butcher on Elite Goats</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="py-32 max-w-screen-2xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-20 items-center">
            <div className="lg:col-span-6 space-y-10">
              <div className="space-y-4">
                <span className="text-primary font-bold tracking-[0.4em] uppercase text-xs block">About the Shop</span>
                <h2 className="text-5xl lg:text-6xl font-headline font-bold text-slate-900 leading-[1.1]">
                  {butcher.name}'s Butcher Shop
                </h2>
                <p className="text-xl text-slate-500 leading-relaxed max-w-xl font-light">
                  {butcher.address || 'Premium quality meats sourced fresh daily. Committed to hygiene, quality, and customer satisfaction.'}
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-8">
                {[
                  { icon: 'verified', title: 'Quality Assured', description: 'All products are fresh, hygienically processed and quality checked.' },
                  { icon: 'local_shipping', title: 'Fast Delivery', description: 'Same-day and next-day delivery available for most locations.' },
                ].map((f, i) => (
                  <div key={i} className="space-y-4 p-8 bg-slate-50 rounded-2xl border border-slate-100">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                      <span className="material-symbols-outlined text-primary">{f.icon}</span>
                    </div>
                    <h4 className="text-lg font-bold text-slate-900">{f.title}</h4>
                    <p className="text-sm text-slate-500 leading-relaxed">{f.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-6 relative">
              <div className="aspect-[4/5] rounded-[2rem] overflow-hidden"
                style={{ boxShadow: '0 30px 60px -15px rgba(0,107,86,0.08)' }}>
                <img
                  src={butcher.profile_image
                    ? `${BASE_URL}${butcher.profile_image}`
                    : `https://ui-avatars.com/api/?name=${encodeURIComponent(butcher.name)}&size=600&background=06A788&color=fff`}
                  alt={butcher.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-10 -left-10 bg-primary p-10 rounded-[2rem] text-white max-w-xs"
                style={{ boxShadow: '0 30px 60px -15px rgba(0,107,86,0.3)' }}>
                <span className="material-symbols-outlined text-4xl mb-4 opacity-50">format_quote</span>
                <p className="italic text-xl mb-4 leading-relaxed font-light">
                  "Fresh cuts, honest prices — that's our promise to every customer."
                </p>
                <p className="font-bold tracking-widest text-xs uppercase opacity-80">— {butcher.name}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Products Section */}
        {butcher.products && butcher.products.length > 0 && (
          <section className="bg-slate-50 py-32 border-y border-slate-100">
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-12">
              <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
                <div className="space-y-3">
                  <span className="text-primary font-bold tracking-[0.4em] uppercase text-xs block">Available Now</span>
                  <h2 className="text-5xl font-headline font-bold text-slate-900">Fresh Products</h2>
                </div>
                <button
                  onClick={() => navigate(`/search?mode=meat`)}
                  className="group flex items-center gap-3 font-bold text-primary hover:text-emerald-600 transition-all uppercase text-sm tracking-widest">
                  View All Products
                  <span className="material-symbols-outlined group-hover:translate-x-2 transition-transform">arrow_right_alt</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                {butcher.products.map((product) => (
                  <div key={product.id}
                    onClick={() => navigate(`/meat-product/${product.public_id || product.id}`)}
                    className="group bg-white rounded-3xl overflow-hidden border border-slate-100 transition-all hover:-translate-y-2 cursor-pointer"
                    style={{ boxShadow: '0 30px 60px -15px rgba(0,107,86,0.08)' }}>
                    <div className="relative h-64 overflow-hidden">
                      {product.image_url
                        ? <img src={getImageUrl(product.image_url)} alt={product.name}
                            className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105" />
                        : <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                            <span className="material-symbols-outlined text-6xl text-slate-300">storefront</span>
                          </div>
                      }
                      <div className="absolute top-4 left-4 bg-primary/90 text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest">
                        {product.category}
                      </div>
                      <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-[10px] font-bold ${
                        product.stock_qty > 0 ? 'bg-black/60 text-white' : 'bg-orange-500 text-white'
                      }`}>
                        {product.stock_qty > 0 ? `Stock: ${product.stock_qty}` : 'Out of Stock'}
                      </div>
                    </div>
                    <div className="p-8 space-y-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="text-xl font-headline font-bold text-slate-900">{product.name}</h4>
                          <p className="text-slate-400 text-sm mt-1">
                            {product.weight_kg ? `${product.weight_kg} ${product.unit || 'kg'}` : product.unit || ''}
                          </p>
                        </div>
                        <p className="text-2xl font-headline font-black text-primary">
                          ₹{Number(product.price_inr).toLocaleString('en-IN')}
                        </p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/meat-product/${product.public_id || product.id}`); }}
                        className="w-full py-4 rounded-full bg-slate-900 text-white font-bold text-sm tracking-widest uppercase hover:bg-primary transition-all shadow-lg">
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Location */}
        <section className="py-32 max-w-screen-2xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mx-auto text-center space-y-10">
            <div className="space-y-4">
              <span className="text-primary font-bold tracking-[0.4em] uppercase text-xs block">Find Us</span>
              <h2 className="text-5xl font-headline font-bold text-slate-900">Shop Location</h2>
            </div>
            <div className="relative rounded-[3rem] overflow-hidden h-96"
              style={{ boxShadow: '0 30px 60px -15px rgba(0,107,86,0.08)' }}>
              <img
                src="https://images.unsplash.com/photo-1524661135-423995f22d0b?w=800"
                alt="Map"
                className="w-full h-full object-cover"
                style={{ filter: 'grayscale(1) opacity(0.2) contrast(1.25)' }}
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center p-12 space-y-6">
                <div className="bg-primary text-white w-20 h-20 rounded-3xl flex items-center justify-center shadow-xl">
                  <span className="material-symbols-outlined text-4xl">location_on</span>
                </div>
                <div className="space-y-2">
                  <p className="text-2xl font-headline font-bold text-slate-900">
                    {[butcher.city, butcher.state].filter(Boolean).join(', ')}
                  </p>
                  <p className="text-slate-500">{butcher.address || 'Contact for exact directions.'}</p>
                </div>
                {butcher.phone && (
                  <a href={`tel:${butcher.phone}`}
                    className="bg-white text-primary px-10 py-4 rounded-full font-bold shadow-xl border border-slate-100 hover:bg-slate-50 transition-all">
                    Call {butcher.phone}
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>

      </main>

      <Footer variant="default" />
    </div>
  );
};

export default ButcherProfile;